import { LiveSubscriptionType } from "../../../types/LiveSubscriptionTypes.ts";
import { differenceInDays, parseISO } from 'date-fns';
import { SubscriptionType } from "../../../../../enums.ts";
import { useAppSelector } from "../../../../../state/state.tsx";
import { selectActiveRole } from "../../../../../state/UserStatesV2/userSliceV2.ts";
type StatusRendererParams = {
    status: string;
    subscriptionType: string;
    remainingCount?: number;
    billingEnd?: string;
};

export const getSubscriptionStatusText = ({
    status,
    subscriptionType,
    remainingCount = 0,
    billingEnd
}: StatusRendererParams): string => {
    // Handle terminated and inactive status first as they're shown the same way for all types
    if (status === "Terminated" || status === "InActive") {
        return status;
    }

    // Handle Count subscription type
    if (subscriptionType === "Count") {
        if (billingEnd) {
            
            try {
                const endDate = parseISO(billingEnd);
               
                if (differenceInDays(endDate, new Date()) <= 0) {
                    return "Expired - Past due date";
                }
            } catch {
                // Continue with normal status handling if date parsing fails
            }
        }
        
        if (status === "Active") {
            if (remainingCount <= 0) {
                return `Active,  Overdue ${remainingCount % 1 === 0 ? remainingCount : remainingCount.toFixed(2)} Classes, Recharge needed!`;
            }
            return `Active ${remainingCount % 1 === 0 ? remainingCount : remainingCount.toFixed(2)} Classes Remaining`;
        }
        if (status === "Paused") {
            if (remainingCount <= 0) {
                return `Paused - ${remainingCount % 1 === 0 ? remainingCount : remainingCount.toFixed(2)} Classes - Recharge needed!`;
            }
            return "Paused";
        }
    }

    // Handle MultiMonth subscription type
    if (subscriptionType === "MultiMonth" && billingEnd) {
        try {
            const endDate = parseISO(billingEnd);
            const daysDifference = differenceInDays(endDate, new Date());
            
            if (daysDifference < 0) {
                return `Expired ${Math.abs(daysDifference)} days ago`;
            }else if (daysDifference === 0) {
                return `Expires Today`
            }
            else if (daysDifference <= 15) {
                return `${status} - Expires in ${daysDifference} days`;
            } else {
                return `${status} - Valid until ${endDate.getDate().toString().padStart(2, '0')}/${(endDate.getMonth() + 1).toString().padStart(2, '0')}/${endDate.getFullYear()}`;
            }
        } catch {
            return status;
        }
    }

    // Default case for regular subscriptions
    return status;
};

const billingEndIsExpired = (billingEnd: string): boolean => {
    try {
        const endDate = parseISO(billingEnd);
        return differenceInDays(endDate, new Date()) < 0;
    } catch {
        return false;
    }
};

/** Tailwind classes for status text; keep in sync with list/detail UIs that use getSubscriptionStatusText. */
export const getSubscriptionStatusColorClass = ({
    status,
    subscriptionType,
    remainingCount,
    billingEnd
}: StatusRendererParams): string => {
    if (status === "InActive" || status === "Terminated") return "text-cc_error";
    if (status === "Paused" && (!billingEnd || !billingEndIsExpired(billingEnd))) return "text-cc_bg-paused";

    if (subscriptionType === "Count") {
        if (remainingCount !== undefined && remainingCount <= 0) {
            return "text-cc_error";
        }
        if (billingEnd) {
            try {
                const daysDifference = differenceInDays(parseISO(billingEnd), new Date());
                if (daysDifference <= 0) return "text-cc_error";
            } catch {
                // fall through
            }
        }
    }

    if (subscriptionType === "MultiMonth" && billingEnd) {
        try {
            const daysDifference = differenceInDays(parseISO(billingEnd), new Date());
            if (daysDifference < 0) return "text-cc_error";
            if (daysDifference <= 15) return "text-cc_error";
            return "text-cc_success";
        } catch {
            return status === "Active" ? "text-cc_success" : "text-cc_error";
        }
    }

    return status === "Active" ? "text-cc_success" : "text-cc_error";
};

export default function LiveSubRenderer({ liveSubDoc, onChangeSelectedLiveSubscriptionid, selectedSubscriptionPrice }: {
    liveSubDoc: LiveSubscriptionType,
    onChangeSelectedLiveSubscriptionid: (id: string) => void,
    selectedSubscriptionPrice: number | string
}) {

    const activeRole = useAppSelector(selectActiveRole);

    const getStatusColor = (status: string, billingEnd?: string): string =>
        getSubscriptionStatusColorClass({
            status,
            subscriptionType: liveSubDoc.subscriptionType,
            remainingCount: liveSubDoc.remainingCount,
            billingEnd: billingEnd ?? liveSubDoc.billingEnd
        });

    const getMMPrice = () => {
        let assignedPlanPrice = 0;
        assignedPlanPrice = liveSubDoc.assignedPlans[liveSubDoc.assignedPlanIndex].price;
        return ((Number(assignedPlanPrice) * liveSubDoc.priceMultiplier)) + liveSubDoc.products.reduce((sum, product) => sum + Number(product.price || 0), 0)
    }

    return (
        <div onClick={() => {
            onChangeSelectedLiveSubscriptionid(liveSubDoc.id)
        }} className={"border-b-[1px] px-2 border-solid bg-cc_bg_app py-2 hover:bg-gray-100 cursor-pointer"}>
            <p className={"max-w-[50%] overflow-x-hidden overflow-ellipsis"}>{liveSubDoc.studentName}</p>
            <div className={"flex justify-between mt-1 pr-2"}>
                <p className={`flex items-center gap-1 ${getStatusColor(liveSubDoc.status, liveSubDoc.billingEnd)}`}>
                    {getSubscriptionStatusText({
                        status: liveSubDoc.status,
                        subscriptionType: liveSubDoc.subscriptionType,
                        remainingCount: liveSubDoc.remainingCount,
                        billingEnd: liveSubDoc.billingEnd
                    })}
                </p>

               {activeRole?.role !== "Observer" && <p>
                   {` ₹${liveSubDoc.serviceType !== "CableTV"
                        ? (liveSubDoc.subscriptionType === SubscriptionType.Count || liveSubDoc.subscriptionType === SubscriptionType.MultiMonth)
                          ? getMMPrice()
                          : ((Number(liveSubDoc.Price || selectedSubscriptionPrice) * liveSubDoc.priceMultiplier) - liveSubDoc.discountNegotiated) + liveSubDoc.products.reduce((sum, product) => sum + Number(product.price || 0), 0)
                        : liveSubDoc.products.reduce((sum, product) => sum + Number(product.price || 0), 0)
                    }`}
                </p>}
            </div>
        </div>
    )
}